import {
    isEmail,
    isPhoneNumber,
    isPostalCode,
    REQUIRED_FIELD_ERROR_TEXT,
    REQUIRED_LAST_AND_FIRST_OR_CONTACT,
    PHONE_NUMBER_ERROR_TEXT,
    CELL_NUMBER_ERROR_TEXT,
    FAX_NUMBER_ERROR_TEXT,
} from "components/utils/validation";
import { formatPhoneNumber } from "components/utils/format";
import { difference, isEmpty, isEqual, omit, set } from "lodash";
import { ReferenceResult } from "types/ReferenceResult";
import {
    ContactValidationResult,
    ApplicationContactsWidgetValidationResult,
    ContactFormData,
    ContractorFormData,
    ContactInboundData,
    ApplicationContactsWidgetValidationParams,
    ContactTypeCode,
} from "./types";
import { ContactType, ContactData, getRequiredFieldsByContactType, isContactDataEmpty, RequiredField } from "components/utils/contacts";
import { RequireContact } from "../../../../../types/RequireContact";
import { ContactRequirements } from "types/ContactRequirements";

export const DEFAULT_LOT_LABEL = "LOT";

export const createContactFormDataObject = (contact: ContactInboundData | undefined): ContactFormData | undefined => {
    if (!contact) {
        return undefined;
    }

    return {
        contactType: contact.contactType,
        contactTitle: contact.contactTitle,
        firstName: contact.firstName,
        lastName: contact.lastName,
        company: contact.company,
        premiseId: contact.premiseId,
        meterId: contact.meterid,
        acct_number: contact.accountNumber,
        lot: contact.lot,

        address: contact.address,
        address_cont: contact.address_cont,
        city: contact.city,
        state: contact.state,
        zip: contact.zip,
        phone: formatPhoneNumber(contact.phone),
        cell: formatPhoneNumber(contact.cell),
        fax: formatPhoneNumber(contact.fax),
        email: contact.email,
        taxId: contact.taxId,
    };
};

export const createContractorFormDataObjectFromContact = (contractor: ContactInboundData | undefined): ContractorFormData | undefined => {
    if (!contractor) {
        return undefined;
    }

    return {
        contactType: String(ContactTypeCode.Contractor),
        contactTitle: contractor.contactTitle,
        company: contractor.company ?? undefined,
        firstName: contractor.firstName ?? undefined,
        lastName: contractor.lastName ?? undefined,
        premiseId: contractor.premiseId,
        meterId: contractor.meterid,
        acct_number: contractor.accountNumber,
        lot: contractor.lot ?? undefined,
        address: contractor.address ?? undefined,
        address_cont: contractor.address_cont,
        city: contractor.city ?? undefined,
        state: contractor.state ?? undefined,
        zip: contractor.zip ?? undefined,
        phone: formatPhoneNumber(contractor.phone) ?? undefined,
        cell: formatPhoneNumber(contractor.cell),
        fax: formatPhoneNumber(contractor.fax),
        email: contractor.email ?? undefined,
        taxId: contractor.taxId,
        contactNumber: contractor.contactNumber ?? undefined,
        origId: contractor.origId ?? undefined,
    };
};

export const getContactTypeByName = (contactTypes: ReferenceResult[], contactTypeName: string): number | undefined => {
    const value = contactTypes.find((t) => String(t.display).toLowerCase() === contactTypeName.toLowerCase())?.val;

    if (value) {
        return Number(value);
    }

    return undefined;
};

export const validateContactData = ({
    contact,
    contactType,
    requirements,
    isCustomerContact,
}: {
    contact: ContactFormData | ContractorFormData;
    contactType: ContactType;
    requirements: ContactRequirements;
    isCustomerContact: boolean;
}) => {
    let errors = null;
    const requiredFields = isCustomerContact
        ? requirements.requiredFields.map((field: RequiredField) => field.field)
        : getRequiredFieldsByContactType(requirements.requiredFields, contactType);

    if (contactType === ContactType.Premise) {
        errors = validateContact(requiredFields, contact);
    }

    if (contactType === ContactType.Primary) {
        errors = validateContact(requiredFields, contact);
    }

    if (contactType === ContactType.Contractor) {
        // Do not validate the form if it is readonly
        if (
            requirements.requireContractor &&
            [RequireContact.RequiredFromList, RequireContact.NotRequiredFromList, RequireContact.ReadOnly].includes(
                requirements.requireContractor
            )
        ) {
            return errors;
        }

        if (
            requirements.requirePrimaryContact === RequireContact.Required ||
            (requirements.requirePrimaryContact === RequireContact.NotRequired && !isContactDataEmpty(contact))
        ) {
            errors = validateContact(requiredFields, contact);
        }

        // Add error if required contractor is not selected
        if (
            requirements.requireContractor &&
            [RequireContact.RequiredFromList].includes(requirements.requireContractor) &&
            isContactDataEmpty(contact)
        ) {
            errors = set(errors ?? {}, "contractorSearch", "Contractor is Required");
        }
    }

    if (isCustomerContact) {
        errors = validateContact(requiredFields, contact);
    }
    return errors;
};

export const validateContact = (requiredFields: string[], contact: ContactFormData | ContractorFormData) => {
    let errors: ContactValidationResult = {};

    if (!contact) {
        return errors;
    }

    const validateRequiredField = (fieldKey: keyof (ContactFormData | ContractorFormData)) => {
        if (requiredFields.includes(fieldKey.toLowerCase()) && isEmpty(contact[fieldKey]?.toString().trim())) {
            set(errors, fieldKey, REQUIRED_FIELD_ERROR_TEXT);
        }
    };

    if (needsFirstAndLastOrCompany(requiredFields)) {
        if (isEmpty(contact.firstName?.trim()) && isEmpty(contact.company?.trim())) {
            let errorFieldKey = "";

            // Get visible required field key
            if (requiredFields.includes("firstname")) {
                errorFieldKey = "firstName";
            } else if (requiredFields.includes("lastname")) {
                errorFieldKey = "lastName";
            } else {
                errorFieldKey = "company";
            }

            set(errors, errorFieldKey, REQUIRED_LAST_AND_FIRST_OR_CONTACT);
        }

        if (isEmpty(contact.lastName?.trim())) {
            if (isEmpty(contact.company?.trim()) && !isEmpty(contact.firstName?.trim())) {
                let errorFieldKey = "";

                // Get visible required field key
                if (requiredFields.includes("lastname")) {
                    errorFieldKey = "lastName";
                } else if (requiredFields.includes("firstname")) {
                    errorFieldKey = "firstName";
                } else {
                    errorFieldKey = "company";
                }

                set(errors, errorFieldKey, REQUIRED_LAST_AND_FIRST_OR_CONTACT);
            }
        }
    } else {
        validateRequiredField("firstName");
        validateRequiredField("lastName");
        validateRequiredField("company");
    }

    validateRequiredField("contactType");
    validateRequiredField("premiseId");
    validateRequiredField("meterId");
    validateRequiredField("acct_number");
    validateRequiredField("lot");
    validateRequiredField("address");
    validateRequiredField("address_cont");
    validateRequiredField("city");
    validateRequiredField("state");
    validateRequiredField("zip");
    validateRequiredField("phone");
    validateRequiredField("cell");
    validateRequiredField("fax");
    validateRequiredField("taxId");
    validateRequiredField("email");
    validateContactDataFormat(contact, errors);

    return isEmpty(errors) ? undefined : errors;
};

export const validate = ({
    primaryContactData,
    premiseContactData,
    contractorContactData,
    requirements,
    contractorLabel,
}: ApplicationContactsWidgetValidationParams): ApplicationContactsWidgetValidationResult => {
    let premiseContactErrors;
    let primaryContactErrors;
    let contractorContactErrors;

    if (premiseContactData && (!isContactDataEmpty(premiseContactData) || requirements.requirePremise === RequireContact.Required)) {
        premiseContactErrors = validateContactData({
            contact: premiseContactData,
            contactType: ContactType.Premise,
            requirements,
            isCustomerContact: false,
        });
    }

    if (primaryContactData && (!isContactDataEmpty(primaryContactData) || requirements.requirePrimaryContact === RequireContact.Required)) {
        primaryContactErrors = validateContactData({
            contact: primaryContactData,
            contactType: ContactType.Primary,
            requirements,
            isCustomerContact: false,
        });
    }

    if (contractorContactData) {
        const skipContractorValidation =
            requirements.requireContractor &&
            [RequireContact.RequiredFromList, RequireContact.NotRequiredFromList, RequireContact.ReadOnly].includes(
                requirements.requireContractor
            );

        if (
            !skipContractorValidation &&
            (!isContactDataEmpty(contractorContactData) || requirements.requireContractor === RequireContact.Required)
        ) {
            contractorContactErrors = validateContactData({
                contact: contractorContactData,
                contactType: ContactType.Contractor,
                requirements,
                isCustomerContact: false,
            });
        }

        // Add error if required contractor is not selected
        if (requirements.requireContractor === RequireContact.RequiredFromList && isContactDataEmpty(contractorContactData)) {
            contractorContactErrors = { contractorSearch: `${contractorLabel} is Required` };
        }
    }

    return {
        primaryContactErrors,
        premiseContactErrors,
        contractorContactErrors,
    };
};

export const validateDataFormat = ({
    primaryContactData,
    premiseContactData,
    contractorContactData,
}: ApplicationContactsWidgetValidationParams): ApplicationContactsWidgetValidationResult => {
    return {
        primaryContactErrors: validateContactDataFormat(primaryContactData),
        premiseContactErrors: validateContactDataFormat(premiseContactData),
        contractorContactErrors: validateContactDataFormat(contractorContactData),
    };
};

export const validateContactDataFormat = (
    contact: ContactFormData | ContractorFormData,
    errors: ContactValidationResult = {}
): ContactValidationResult => {
    if (!contact) {
        return undefined;
    }

    if (!isEmpty(contact.phone) && !isPhoneNumber(contact.phone!)) {
        errors.phone = PHONE_NUMBER_ERROR_TEXT;
    }

    if (!isEmpty(contact.cell) && !isPhoneNumber(contact.cell!)) {
        errors.cell = CELL_NUMBER_ERROR_TEXT;
    }

    if (!isEmpty(contact.fax) && !isPhoneNumber(contact.fax!)) {
        errors.fax = FAX_NUMBER_ERROR_TEXT;
    }

    if (!isEmpty(contact.email) && !isEmail(contact.email ?? "")) {
        errors.email = "Enter an email address in the correct format, like name@example.com";
    }

    if (!isEmpty(contact.zip) && !isPostalCode(contact.zip ?? "")) {
        errors.zip = "Enter a postal code in the correct format, like 00000, 00000-0000, or A0A 0A0";
    }

    return isEmpty(errors) ? undefined : errors;
};

/**
 *  Return "copy contact" checkbox state.
 *
 * @param premiseContactFormRef Premise contact form ref
 * @param primaryContactFormRef Primary contact form ref
 * @returns  Return true if both contact forms have some data and are equal. Otherwise, false.
 */
export const getCopyContactState = (formData: any, primaryContact: any) => {
    const omitFields = ["copyContact", "saveContact", "storedContactNumber", "contactType", "contactTitle"];
    const premiseContactData = omit(formData, omitFields) as Partial<ContactData>;
    const primaryContactData = omit(primaryContact, omitFields) as Partial<ContactData>;

    return (
        isEqual(premiseContactData, primaryContactData) &&
        !Object.values(premiseContactData).every(isEmpty) &&
        !Object.values(primaryContactData).every(isEmpty)
    );
};

export const getContactName = (contact: ContactInboundData) => {
    const fullName = [contact.firstName, contact.lastName].filter((i) => !isEmpty(i)).join(" ");

    const fullAddress = [contact.address, contact.address_cont].filter((i) => !isEmpty(i)).join(" ");

    return [fullName, contact.company, fullAddress].filter((i) => !isEmpty(i)).join(", ");
};

export const needsFirstAndLastOrCompany = (requiredFields: any[]) =>
    difference(["firstname", "lastname", "company"], requiredFields).length === 0;

export const getReqFields = (requiredFields: any[], formData: any) => {
    if (needsFirstAndLastOrCompany(requiredFields)) {
        return getFLCReqFields(formData);
    } else if (difference(["firstname", "lastname"], requiredFields).length === 0 && !requiredFields.includes("company")) {
        return ["firstname", "lastname"];
    } else if (
        difference(["company"], requiredFields).length === 0 &&
        !requiredFields.includes("firstname") &&
        !requiredFields.includes("lastname")
    ) {
        return ["company"];
    }
    return [];
};

const getFLCReqFields = (formData: any) => {
    const contactData = formData;
    let FLCReqFields: Array<string> = [];
    if (contactData?.company || (contactData?.firstName && contactData?.lastName)) {
        FLCReqFields = [];
    } else if (contactData?.firstName && !contactData?.lastName) {
        FLCReqFields = ["lastname"];
    } else if (!contactData?.firstName && contactData?.lastName) {
        FLCReqFields = ["firstname"];
    }
    return FLCReqFields;
};

/**
 * Return stored contact contactNumber if form data is equal to stored contact..
 * Otherwise, return undefined
 * */
export const getStoredContactState = (formData: any, customerContacts: ContactInboundData[], isContractorForm: boolean = false) => {
    if (!isEmpty(formData.storedContactNumber)) {
        const contact = customerContacts.find((c) => c.contactNumber === formData.storedContactNumber);
        if (contact) {
            const contactData = isContractorForm
                ? createContractorFormDataObjectFromContact(contact)
                : createContactFormDataObject(contact);
            const contactFormData = omit(formData, ["copyContact", "saveContact", "storedContactNumber"]);

            if (isEqual(contactFormData, contactData)) {
                return contact.contactNumber;
            }
        }
    }

    return undefined;
};
